export const GAME_BRIDGE_SOURCE = "zsombzilla-game";
export const GAME_BRIDGE_PROTOCOL_VERSION = 1;

const SESSION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type GameBridgeEnvelope<Type extends string, Payload> = {
  source: typeof GAME_BRIDGE_SOURCE;
  protocolVersion: typeof GAME_BRIDGE_PROTOCOL_VERSION;
  type: Type;
  payload: Payload;
};

export type GameReadyEvent = GameBridgeEnvelope<
  "gameReady",
  {
    gameVersion: string;
    supportedEvents: string[];
  }
>;

export type GameStartedEvent = GameBridgeEnvelope<
  "gameStarted",
  {
    gameSessionId: string;
    gameVersion: string;
  }
>;

export type GameFinishedEvent = GameBridgeEnvelope<
  "gameFinished",
  {
    gameSessionId: string;
    score: number;
    outcome: "won" | "lost";
    durationMs: number;
    gameVersion: string;
  }
>;

export type GameBridgeEvent = GameReadyEvent | GameStartedEvent | GameFinishedEvent;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isSessionId(value: unknown): value is string {
  return typeof value === "string" && SESSION_ID_PATTERN.test(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

function parseMessageData(data: unknown): unknown {
  if (typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data) as unknown;
  } catch {
    return null;
  }
}

export function parseGameBridgeEvent(data: unknown): GameBridgeEvent | null {
  const event = parseMessageData(data);
  if (
    !isRecord(event) ||
    event.source !== GAME_BRIDGE_SOURCE ||
    event.protocolVersion !== GAME_BRIDGE_PROTOCOL_VERSION ||
    !isRecord(event.payload)
  ) {
    return null;
  }

  const payload = event.payload;
  if (event.type === "gameReady") {
    if (
      !isNonEmptyString(payload.gameVersion) ||
      !Array.isArray(payload.supportedEvents) ||
      !payload.supportedEvents.every(isNonEmptyString)
    ) {
      return null;
    }
    return {
      source: GAME_BRIDGE_SOURCE,
      protocolVersion: GAME_BRIDGE_PROTOCOL_VERSION,
      type: "gameReady",
      payload: {
        gameVersion: payload.gameVersion,
        supportedEvents: payload.supportedEvents,
      },
    };
  }

  if (event.type === "gameStarted") {
    if (!isSessionId(payload.gameSessionId) || !isNonEmptyString(payload.gameVersion)) {
      return null;
    }
    return {
      source: GAME_BRIDGE_SOURCE,
      protocolVersion: GAME_BRIDGE_PROTOCOL_VERSION,
      type: "gameStarted",
      payload: {
        gameSessionId: payload.gameSessionId,
        gameVersion: payload.gameVersion,
      },
    };
  }

  if (event.type === "gameFinished") {
    if (
      !isSessionId(payload.gameSessionId) ||
      !isNonNegativeInteger(payload.score) ||
      (payload.outcome !== "won" && payload.outcome !== "lost") ||
      !isNonNegativeInteger(payload.durationMs) ||
      !isNonEmptyString(payload.gameVersion)
    ) {
      return null;
    }
    return {
      source: GAME_BRIDGE_SOURCE,
      protocolVersion: GAME_BRIDGE_PROTOCOL_VERSION,
      type: "gameFinished",
      payload: {
        gameSessionId: payload.gameSessionId,
        score: payload.score,
        outcome: payload.outcome,
        durationMs: payload.durationMs,
        gameVersion: payload.gameVersion,
      },
    };
  }

  return null;
}
